import { TrashIcon } from '@heroicons/react/16/solid';
import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import {
  INTERACTIVE_LIMITS,
  tInteractiveOutbound,
} from '../../../../../../Chat.types';
import { slugifyId, totalListRowCount } from '../../InteractiveAction.functions';
import UTF16Counter from '../UTF16Counter/UTF16Counter.component';

// List message: sections of rows. Meta caps total rows at 10 across ALL
// sections (not 10 per section — this is an easy mistake). Day-1 supports
// multiple sections; can de-scope to single-section if QA flags complexity.
export default function ListBuilder() {
  const { t } = useTranslation(['chat/chat']);
  const { values, handleChange, setFieldValue } =
    useFormikContext<tInteractiveOutbound>();
  // Tracks which rows the seller has manually edited the id for. Keyed by
  // "sectionIdx:rowIdx" so shifts in either dimension don't collide.
  const [manualRowIds, setManualRowIds] = useState<Set<string>>(new Set());
  const [openAdvanced, setOpenAdvanced] = useState<Set<string>>(new Set());
  if (values.type !== 'list') return null;

  function toggleRowAdvanced(key: string) {
    setOpenAdvanced((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const sections = values.action.sections;
  const totalRows = totalListRowCount(sections);
  const canAddSection =
    sections.length < INTERACTIVE_LIMITS.LIST_SECTION_COUNT_MAX;
  const canAddRow = totalRows < INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MAX;

  return (
    <div className="InteractiveAction__Builder">
      <p className="InteractiveAction__Builder__Title">
        {t('interactive.list.title')}
      </p>

      <div className="InteractiveAction__Field">
        <label className="InteractiveAction__Field__Label">
          {t('interactive.list.buttonTextLabel')}
        </label>
        <input
          type="text"
          dir="auto"
          name="action.button_text"
          maxLength={INTERACTIVE_LIMITS.LIST_BUTTON_TEXT}
          className="InteractiveAction__Field__Input"
          placeholder={t('interactive.list.buttonTextPlaceholder')}
          value={values.action.button_text}
          onChange={handleChange}
        />
        <UTF16Counter
          value={values.action.button_text}
          max={INTERACTIVE_LIMITS.LIST_BUTTON_TEXT}
        />
      </div>

      <p className="InteractiveAction__Field__Counter">
        {t('interactive.list.totalRows', {
          count: totalRows,
          max: INTERACTIVE_LIMITS.LIST_ROWS_TOTAL_MAX,
        })}
      </p>

      <FieldArray name="action.sections">
        {({ push: pushSection, remove: removeSection }) => (
          <>
            {sections.map((section, sIdx) => (
              <div
                key={sIdx}
                className="InteractiveAction__Section"
                data-testid={`interactive-section-${sIdx}`}
              >
                <div className="InteractiveAction__Field">
                  <label className="InteractiveAction__Field__Label">
                    {t('interactive.list.sectionTitleLabel', { index: sIdx + 1 })}
                  </label>
                  <input
                    type="text"
                    dir="auto"
                    name={`action.sections[${sIdx}].title`}
                    maxLength={INTERACTIVE_LIMITS.LIST_SECTION_TITLE}
                    className="InteractiveAction__Field__Input"
                    placeholder={t('interactive.list.sectionTitlePlaceholder')}
                    value={section.title ?? ''}
                    onChange={handleChange}
                  />
                  <UTF16Counter
                    value={section.title}
                    max={INTERACTIVE_LIMITS.LIST_SECTION_TITLE}
                  />
                </div>

                <FieldArray name={`action.sections[${sIdx}].rows`}>
                  {({ push: pushRow, remove: removeRow }) => (
                    <>
                      {section.rows.map((row, rIdx) => {
                        const manualKey = `${sIdx}:${rIdx}`;
                        const isAdvancedOpen = openAdvanced.has(manualKey);
                        const canRemove = section.rows.length > 1;
                        return (
                          <div
                            key={rIdx}
                            className="InteractiveAction__Builder__Row"
                            data-testid={`interactive-row-${sIdx}-${rIdx}`}
                          >
                            <div className="InteractiveAction__Builder__RowHeader">
                              <label className="InteractiveAction__Builder__RowHeader__Label">
                                {t('interactive.list.rowTitleLabel')}
                              </label>
                              <button
                                type="button"
                                className="InteractiveAction__Builder__RowHeader__Advanced"
                                aria-expanded={isAdvancedOpen}
                                onClick={() => toggleRowAdvanced(manualKey)}
                              >
                                {isAdvancedOpen ? '▾ ' : '▸ '}
                                {t('interactive.list.advancedToggle')}
                              </button>
                              {canRemove && (
                                <button
                                  type="button"
                                  className="InteractiveAction__Builder__RowHeader__Remove"
                                  title={t('interactive.list.removeRow')}
                                  aria-label={t('interactive.list.removeRow')}
                                  onClick={() => {
                                    removeRow(rIdx);
                                    // Reset manual-tracking rather than
                                    // shift-remap keys; see ReplyButtonsBuilder
                                    // for the same rationale.
                                    setManualRowIds(new Set());
                                    setOpenAdvanced(new Set());
                                  }}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>

                            <input
                              type="text"
                              dir="auto"
                              name={`action.sections[${sIdx}].rows[${rIdx}].title`}
                              maxLength={INTERACTIVE_LIMITS.LIST_ROW_TITLE}
                              className="InteractiveAction__Field__Input"
                              placeholder={t('interactive.list.rowTitlePlaceholder')}
                              value={row.title}
                              onChange={(e) => {
                                const nextTitle = e.target.value;
                                setFieldValue(
                                  `action.sections[${sIdx}].rows[${rIdx}].title`,
                                  nextTitle,
                                );
                                if (!manualRowIds.has(manualKey)) {
                                  setFieldValue(
                                    `action.sections[${sIdx}].rows[${rIdx}].id`,
                                    slugifyId(nextTitle),
                                  );
                                }
                              }}
                            />
                            <UTF16Counter
                              value={row.title}
                              max={INTERACTIVE_LIMITS.LIST_ROW_TITLE}
                            />

                            <label className="InteractiveAction__Field__Label InteractiveAction__Builder__SubLabel">
                              {t('interactive.list.rowDescriptionLabel')}
                            </label>
                            <input
                              type="text"
                              dir="auto"
                              name={`action.sections[${sIdx}].rows[${rIdx}].description`}
                              maxLength={INTERACTIVE_LIMITS.LIST_ROW_DESCRIPTION}
                              className="InteractiveAction__Field__Input"
                              placeholder={t('interactive.list.rowDescriptionPlaceholder')}
                              value={row.description ?? ''}
                              onChange={handleChange}
                            />
                            <UTF16Counter
                              value={row.description}
                              max={INTERACTIVE_LIMITS.LIST_ROW_DESCRIPTION}
                            />

                            {isAdvancedOpen && (
                              <div className="InteractiveAction__Advanced__Content">
                                <label className="InteractiveAction__Field__Label">
                                  {t('interactive.list.rowIdLabel')}
                                </label>
                                <input
                                  type="text"
                                  name={`action.sections[${sIdx}].rows[${rIdx}].id`}
                                  maxLength={INTERACTIVE_LIMITS.LIST_ROW_ID}
                                  className="InteractiveAction__Field__Input"
                                  placeholder={t('interactive.list.rowIdPlaceholder')}
                                  value={row.id}
                                  onChange={(e) => {
                                    handleChange(e);
                                    setManualRowIds((prev) => {
                                      const next = new Set(prev);
                                      next.add(manualKey);
                                      return next;
                                    });
                                  }}
                                />
                                <p className="InteractiveAction__Advanced__Help">
                                  {t('interactive.list.rowIdHelp')}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {canAddRow && (
                        <Button
                          type="button"
                          color="ghost"
                          size="sm"
                          className="InteractiveAction__Builder__AddButton"
                          onClick={() => pushRow({ id: '', title: '' })}
                        >
                          {t('interactive.list.addRow')}
                        </Button>
                      )}
                    </>
                  )}
                </FieldArray>

                {sections.length > 1 && (
                  <button
                    type="button"
                    className="InteractiveAction__Builder__RemoveButton"
                    onClick={() => removeSection(sIdx)}
                  >
                    <TrashIcon className="inline w-4 h-4" />{' '}
                    {t('interactive.list.removeSection')}
                  </button>
                )}
              </div>
            ))}

            {canAddSection && canAddRow && (
              <Button
                type="button"
                color="ghost"
                size="sm"
                className="InteractiveAction__Builder__AddButton"
                onClick={() =>
                  pushSection({
                    title: '',
                    rows: [{ id: '', title: '' }],
                  })
                }
              >
                {t('interactive.list.addSection')}
              </Button>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
}
